<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { formatBytes } from 'shared/helpers/FileHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
  draftFiles: {
    type: Array,
    default: () => [],
  },
  uploadingFiles: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'removeAttachment',
  'removeDraftAttachment',
  'cancelUpload',
]);

const store = useStore();

const onRemoveAttachment = itemIndex => {
  emit(
    'removeAttachment',
    props.attachments.filter((_, index) => index !== itemIndex)
  );
};

const formatFileSize = file => {
  const size = file.byte_size || file.size;
  return formatBytes(size, 0);
};

const isTypeImage = file => {
  const type = file.content_type || file.type;
  return type.includes('image');
};

const fileName = file => {
  return file.filename || file.name;
};

const showFullNames = computed(() => {
  const currentAccount = store.getters['accounts/getCurrentAccount'];
  return currentAccount?.show_full_attachment_names || false;
});

const onRemoveDraftAttachment = attachmentId => {
  emit('removeDraftAttachment', attachmentId);
};

const onCancelUpload = tempId => {
  emit('cancelUpload', tempId);
};

const draftFileName = file => file.filename || file.name;
const draftFileSize = file => formatBytes(file.byte_size || 0, 0);
const isDraftImage = file => file.content_type?.includes('image');
</script>

<template>
  <div class="flex flex-wrap gap-2">
    <div
      v-for="draftFile in draftFiles"
      :key="`draft-${draftFile.id}`"
      class="preview-item flex items-center bg-slate-50 dark:bg-slate-800 gap-1 rounded-md"
      :class="[showFullNames ? 'p-2 min-w-[15rem]' : 'p-1 w-[15rem]']"
    >
      <div class="flex-shrink-0 w-6 flex items-center">
        <img
          v-if="isDraftImage(draftFile)"
          class="object-cover w-6 h-6 rounded-sm"
          :src="draftFile.url"
        />
        <span v-else class="relative w-6 h-6 text-lg text-left -top-px">
          📄
        </span>
      </div>
      <div
        class="flex-1 min-w-0"
        :class="showFullNames ? '' : 'max-w-[60%] overflow-hidden'"
      >
        <span
          class="text-sm font-medium block"
          :class="
            showFullNames
              ? 'break-all'
              : 'overflow-hidden whitespace-nowrap text-ellipsis'
          "
        >
          {{ draftFileName(draftFile) }}
        </span>
      </div>
      <div
        class="justify-center"
        :class="showFullNames ? 'flex-shrink-0' : 'w-[30%]'"
      >
        <span class="overflow-hidden text-xs text-ellipsis whitespace-nowrap">
          {{ draftFileSize(draftFile) }}
        </span>
      </div>
      <div class="flex items-center justify-center flex-shrink-0">
        <Button
          type="button"
          ghost
          slate
          xs
          icon="i-lucide-x"
          @click="onRemoveDraftAttachment(draftFile.id)"
        />
      </div>
    </div>
    <div
      v-for="(attachment, index) in attachments"
      :key="attachment.id"
      class="preview-item flex items-center bg-slate-50 dark:bg-slate-800 gap-1 rounded-md"
      :class="[showFullNames ? 'p-2 min-w-[15rem]' : 'p-1 w-[15rem]']"
    >
      <div class="flex-shrink-0 w-6 flex items-center">
        <img
          v-if="isTypeImage(attachment.resource)"
          class="object-cover w-6 h-6 rounded-sm"
          :src="attachment.thumb"
        />
        <span v-else class="relative w-6 h-6 text-lg text-left -top-px">
          📄
        </span>
      </div>
      <div
        class="flex-1 min-w-0"
        :class="showFullNames ? '' : 'max-w-[60%] overflow-hidden'"
      >
        <span
          class="text-sm font-medium block"
          :class="
            showFullNames
              ? 'break-all'
              : 'overflow-hidden whitespace-nowrap text-ellipsis'
          "
        >
          {{ fileName(attachment.resource) }}
        </span>
      </div>
      <div
        class="justify-center"
        :class="showFullNames ? 'flex-shrink-0' : 'w-[30%]'"
      >
        <span class="overflow-hidden text-xs text-ellipsis whitespace-nowrap">
          {{ formatFileSize(attachment.resource) }}
        </span>
      </div>
      <div class="flex items-center justify-center flex-shrink-0">
        <Button
          type="button"
          ghost
          slate
          xs
          icon="i-lucide-x"
          @click="onRemoveAttachment(index)"
        />
      </div>
    </div>
    <div
      v-for="uploadingFile in uploadingFiles"
      :key="`uploading-${uploadingFile.tempId}`"
      class="preview-item flex items-center bg-slate-50 dark:bg-slate-800 gap-1 rounded-md opacity-60"
      :class="[showFullNames ? 'p-2 min-w-[15rem]' : 'p-1 w-[15rem]']"
    >
      <div class="flex-shrink-0 w-6 flex items-center justify-center">
        <Spinner size="small" />
      </div>
      <div
        class="flex-1 min-w-0"
        :class="showFullNames ? '' : 'max-w-[60%] overflow-hidden'"
      >
        <span
          class="text-sm font-medium block"
          :class="
            showFullNames
              ? 'break-all'
              : 'overflow-hidden whitespace-nowrap text-ellipsis'
          "
        >
          {{ uploadingFile.filename }}
        </span>
      </div>
      <div
        class="justify-center"
        :class="showFullNames ? 'flex-shrink-0' : 'w-[30%]'"
      >
        <span class="overflow-hidden text-xs text-ellipsis whitespace-nowrap">
          {{ formatBytes(uploadingFile.byte_size || 0, 0) }}
        </span>
      </div>
      <div class="flex items-center justify-center flex-shrink-0">
        <Button
          type="button"
          ghost
          slate
          xs
          icon="i-lucide-x"
          @click="onCancelUpload(uploadingFile.tempId)"
        />
      </div>
    </div>
  </div>
</template>
